import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase-config';

const NotificationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [relatedNotifications, setRelatedNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const loadNotification = async () => {
      setLoading(true);
      try {
        if (!id) {
          navigate('/');
          return;
        }

        const notifRef = doc(db, 'notifications', id);
        const notifSnap = await getDoc(notifRef);
        
        if (notifSnap.exists()) {
          const data = { id: notifSnap.id, ...notifSnap.data() };
          setNotification(data);

          // Load related notifications
          const q = query(
            collection(db, 'notifications'),
            orderBy('createdAt', 'desc'),
            limit(5)
          );
          const relatedSnap = await getDocs(q);
          const relatedData = [];
          relatedSnap.forEach(doc => {
            if (doc.id !== id) {
              relatedData.push({ id: doc.id, ...doc.data() });
            }
          });
          setRelatedNotifications(relatedData);
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Error loading notification:', error);
        navigate('/');
      }
      setLoading(false);
    };
    loadNotification();
  }, [id, navigate]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recent';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract file path from Firebase Storage URL
  const getFilePathFromUrl = (url) => {
    try {
      // Example: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/notifications%2Ffile.pdf?alt=media&token=TOKEN
      const match = url.match(/\/o\/([^?]+)/);
      if (match) {
        // Decode the URI component to get the actual path
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch (error) {
      console.error('Error extracting file path:', error);
      return null;
    }
  };

  // Download file using Firebase Storage
  const downloadFile = async (fileUrl, fileName) => {
    if (!fileUrl) {
      alert('No file available for download');
      return;
    }

    setDownloading(true);
    try {
      const storage = getStorage();
      let downloadUrl;
      
      // Check if it's a Firebase Storage URL
      if (fileUrl.includes('firebasestorage.googleapis.com')) {
        // Extract the file path from the URL
        const filePath = getFilePathFromUrl(fileUrl);
        if (filePath) {
          // Create a reference using the file path
          const fileRef = ref(storage, filePath);
          // Get the download URL (this generates a new token)
          downloadUrl = await getDownloadURL(fileRef);
        } else {
          // If we can't extract the path, try using the URL directly
          downloadUrl = fileUrl;
        }
      } else {
        // If it's not a Firebase Storage URL, use it directly
        downloadUrl = fileUrl;
      }

      // Create a hidden anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'document.pdf';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      setDownloading(false);
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloading(false);
      
      // If the above fails, try a fallback method
      try {
        // Fallback: try using the stored URL directly with a download attribute
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName || 'document.pdf';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      } catch (fallbackError) {
        alert('Error downloading file. Please try again.');
      }
    }
  };

  // Share functions
  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(notification?.title || 'Check out this notification');
    const url = encodeURIComponent(window.location.href);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`${notification?.title}\n\nRead more: ${window.location.href}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          borderRadius: '50%', 
          border: '4px solid rgba(14,165,233,0.1)', 
          borderTopColor: 'var(--clr-blue)', 
          animation: 'spin 0.8s linear infinite' 
        }}></div>
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 0' }}>
        <i className="fas fa-exclamation-circle" style={{ fontSize: '3rem', color: 'var(--clr-rose)' }}></i>
        <h2 style={{ marginTop: '20px', fontFamily: "'Playfair Display', serif" }}>Notification Not Found</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>The notification you're looking for doesn't exist or has been removed.</p>
        <Link to="/" className="btn-back" style={{
          display: 'inline-block',
          padding: '12px 28px',
          background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)',
          borderRadius: '40px',
          color: 'white',
          fontWeight: 600,
          textDecoration: 'none',
          transition: 'all 0.3s ease'
        }}>
          <i className="fas fa-arrow-left"></i> Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="container notification-detail-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Back Button */}
        <Link to="/" className="back-button">
          <i className="fas fa-arrow-left"></i> Back to Home
        </Link>

        {/* Notification Card */}
        <div className="notification-card">
          <div className="notification-header">
            <div className="notification-badge">
              <i className="fas fa-bell"></i> Announcement
            </div>
            <h1 className="notification-title">{notification.title || 'Notification'}</h1>
            <div className="notification-meta">
              <span>
                <i className="far fa-calendar-alt"></i> {formatDate(notification.createdAt)}
              </span>
              <span>
                <i className="far fa-clock"></i> {formatTime(notification.createdAt)}
              </span>
            </div>
          </div>

          <div className="notification-body">
            <div className="notification-content">
              {notification.description ? (
                notification.description.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))
              ) : (
                <p>No detailed description available for this notification.</p>
              )}
            </div>

            {/* Attachments Section - Download Only */}
            {notification.fileUrl && (
              <div className="notification-attachments">
                <h3><i className="fas fa-paperclip"></i> Attachments</h3>
                <div className="attachment-item">
                  <div className="attachment-info">
                    <div className="attachment-icon">
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <div className="attachment-details">
                      <div className="attachment-name">{notification.title || 'Document'} - Official Document</div>
                      <div className="attachment-size">PDF Document</div>
                    </div>
                  </div>
                  <div className="attachment-actions">
                    <button 
                      onClick={() => downloadFile(notification.fileUrl, `${notification.title || 'document'}.pdf`)}
                      className="attachment-btn download-btn"
                      disabled={downloading}
                    >
                      {downloading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Downloading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-download"></i> Download
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Share Section */}
          <div className="share-section">
            <h4><i className="fas fa-share-alt"></i> Share this notification</h4>
            <div className="share-buttons">
              <button onClick={shareOnFacebook} className="share-btn facebook">
                <i className="fab fa-facebook-f"></i>
              </button>
              <button onClick={shareOnTwitter} className="share-btn twitter">
                <i className="fab fa-twitter"></i>
              </button>
              <button onClick={shareOnWhatsApp} className="share-btn whatsapp">
                <i className="fab fa-whatsapp"></i>
              </button>
              <button onClick={shareOnLinkedIn} className="share-btn linkedin">
                <i className="fab fa-linkedin-in"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Related Notifications */}
        {relatedNotifications.length > 0 && (
          <div className="related-section">
            <h2 className="related-title">
              <i className="fas fa-bell" style={{ color: 'var(--clr-blue)' }}></i> Related Notifications
            </h2>
            <div className="related-grid">
              {relatedNotifications.map((item) => (
                <Link key={item.id} to={`/notification/${item.id}`} className="related-card">
                  <div className="related-content">
                    <h3>{item.title || 'Notification'}</h3>
                    <p>{(item.description || '').substring(0, 100)}...</p>
                    <span className="related-date">
                      <i className="far fa-calendar-alt"></i> {formatDate(item.createdAt)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      <style>{`
        .notification-detail-page {
          padding-top: 40px;
          padding-bottom: 60px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 30px;
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 50px;
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: translateX(-5px);
          border-color: var(--clr-blue);
        }

        .notification-card {
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 8px 32px rgba(14, 165, 233, 0.06);
        }

        .notification-header {
          padding: 32px 40px;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.08), rgba(14, 165, 233, 0.02));
          border-bottom: 1px solid rgba(14, 165, 233, 0.1);
        }

        .notification-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          color: white;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .notification-title {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          color: var(--text-primary);
          margin-bottom: 12px;
          line-height: 1.3;
        }

        .notification-meta {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .notification-meta span {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .notification-meta span i {
          color: var(--clr-blue);
        }

        .notification-body {
          padding: 40px;
        }

        .notification-content p {
          color: var(--text-secondary);
          font-size: 1rem;
          line-height: 1.8;
          margin-bottom: 16px;
        }

        .notification-content p:last-child {
          margin-bottom: 0;
        }

        .notification-attachments {
          margin-top: 35px;
          padding-top: 25px;
          border-top: 2px solid var(--clr-blue-border);
          background: rgba(14, 165, 233, 0.03);
          border-radius: 16px;
          padding: 25px;
        }

        .notification-attachments h3 {
          font-size: 1.1rem;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--clr-blue);
        }

        .attachment-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid var(--clr-blue-border);
          border-radius: 16px;
          padding: 16px 20px;
          transition: all 0.3s ease;
          flex-wrap: wrap;
          gap: 15px;
        }

        .attachment-item:hover {
          background: white;
          transform: translateX(5px);
          border-color: var(--clr-emerald);
        }

        .attachment-info {
          display: flex;
          align-items: center;
          gap: 15px;
          flex: 1;
        }

        .attachment-icon {
          width: 48px;
          height: 48px;
          background: rgba(16, 185, 129, 0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--clr-emerald);
          flex-shrink: 0;
        }

        .attachment-details {
          flex: 1;
        }

        .attachment-name {
          font-weight: 700;
          font-size: 0.95rem;
          margin-bottom: 4px;
          color: var(--text-primary);
        }

        .attachment-size {
          font-size: 0.7rem;
          color: var(--text-muted);
        }

        .attachment-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .attachment-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          border-radius: 40px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          border: none;
        }

        .attachment-btn.download-btn {
          background: linear-gradient(135deg, var(--clr-emerald), #059669);
          color: white;
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }

        .attachment-btn.download-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
        }

        .attachment-btn.download-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .share-section {
          padding: 30px 40px 40px;
          border-top: 1px solid var(--clr-blue-border);
        }

        .share-section h4 {
          font-size: 1rem;
          margin-bottom: 15px;
          color: var(--text-primary);
        }

        .share-section h4 i {
          color: var(--clr-blue);
          margin-right: 8px;
        }

        .share-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .share-btn {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
        }

        .share-btn:hover {
          transform: translateY(-3px);
        }

        .share-btn.facebook { background: #1877f2; }
        .share-btn.twitter { background: #1da1f2; }
        .share-btn.whatsapp { background: #25D366; }
        .share-btn.linkedin { background: #0077b5; }

        .related-section {
          margin-top: 50px;
        }

        .related-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          margin-bottom: 25px;
          position: relative;
        }

        .related-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, var(--clr-blue), transparent);
        }

        .related-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
          margin-top: 20px;
        }

        .related-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid var(--clr-blue-border);
          transition: all 0.3s ease;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
        }

        .related-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
          border-color: var(--clr-blue);
        }

        .related-card h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 8px;
          color: var(--text-primary);
        }

        .related-card p {
          font-size: 0.85rem;
          color: var(--text-secondary);
          line-height: 1.5;
          margin-bottom: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .related-date {
          font-size: 0.75rem;
          color: var(--clr-blue);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        @media (max-width: 768px) {
          .notification-detail-page {
            padding-top: 20px;
            padding-bottom: 40px;
          }

          .notification-header {
            padding: 24px 20px;
          }

          .notification-title {
            font-size: 1.5rem;
          }

          .notification-body {
            padding: 24px 20px;
          }

          .notification-meta {
            gap: 16px;
          }

          .notification-meta span {
            font-size: 0.75rem;
          }

          .share-section {
            padding: 20px 24px 24px;
          }

          .attachment-item {
            flex-direction: column;
            align-items: stretch;
          }

          .attachment-info {
            flex-direction: column;
            text-align: center;
          }

          .attachment-actions {
            justify-content: center;
          }

          .related-grid {
            grid-template-columns: 1fr;
          }

          .back-button {
            padding: 8px 16px;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .notification-title {
            font-size: 1.3rem;
          }

          .notification-badge {
            font-size: 0.65rem;
            padding: 4px 12px;
          }

          .notification-meta {
            flex-direction: column;
            gap: 8px;
          }

          .share-buttons {
            justify-content: center;
          }

          .attachment-btn.download-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .fa-spin {
          animation: fa-spin 1s linear infinite;
        }

        @keyframes fa-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NotificationDetail;